import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { Employee, mapEmployees } from "../Types/EmployeeInterface";
import { mapProfessionals, Professional } from "../Types/ProfessionalInterface";
import { ClientProject } from "../Types/ClientProjectInterface";
import { Client, mapClients } from "../Types/ClientInterface";

interface ContactSliceType {
    status: number;
    activeTab: string;
    employees: Employee[];
    professionals: Professional[];
    clients: Client[];
    loading: boolean;
    error: string | null;
    selectedEmployee: Employee | null;
    selectedProfessional: Professional | null;
    clientContactId: number;
    currentComClientTab: string;
    comClientProjectList: ClientProject[];
    selectedComClient: Client | null;
    selectedClientProject: ClientProject | null;
    clientProjectStatus: number; // 0 - intial 1 - edited
    currentDomClientTab: string;
    selectedDomClient: Client | null;
}

const initialState: ContactSliceType = {
    status: 0,
    activeTab: 'all',
    employees: [],
    professionals: [],
    clients: [],
    loading: false,
    error: null,
    selectedEmployee: null, 
    selectedProfessional: null, 
    clientContactId: 0,
    currentComClientTab: 'Personal',
    comClientProjectList: [],
    selectedComClient : null,
    selectedDomClient : null,
    selectedClientProject : null,
    clientProjectStatus: 0,
    currentDomClientTab: 'Personal'
}

const contactSlice = createSlice ({
    name: 'contactSlice',
    initialState,
    reducers : {
        setStatus: (state, action: PayloadAction<number>) => {
            state.status = action.payload;
        },

        setActiveTab: (state, action: PayloadAction<string>) => {
            state.activeTab = action.payload;
        },

        fetchContactStart: (state) => {
            state.loading = true;
            state.employees = [];
            state.professionals = [];
            state.clients = [];
            state.error = null;
        },

        fetchContactSuccess: (state, action: PayloadAction<any[]>) => {
            state.loading = false;
            
            state.employees = mapEmployees(action.payload.filter(contact => contact.contact_type === 'Employee'));
            state.professionals = mapProfessionals(action.payload.filter(contact => contact.contact_type === 'Professional'));
            state.clients = mapClients(action.payload.filter(contact => contact.contact_type === 'Client'));
        },

        fetchContactFailure: (state, action: PayloadAction<string>) => {
            state.loading = false;
            state.error = action.payload;
        },

        setSelectedEmployee: (state, action: PayloadAction<Employee>) => {
            state.selectedEmployee = action.payload;
        },

        fetchContactSuccessAndSetSelectedEmployee: (state, action: PayloadAction<any[]>) => {
            state.loading = false;
            state.employees = mapEmployees(action.payload.filter(contact => contact.contact_type === 'Employee'));

            const previousSelectedId = state.selectedEmployee?.contactId;
            if (previousSelectedId) {
                state.selectedEmployee = state.employees.find(employee => employee.contactId === previousSelectedId) || null;
            } else {
                state.selectedEmployee = null; // Or set to the first employee in the list, etc.
            }
        },

        setSelectedProfessional: (state, action: PayloadAction<Professional>) => {
            state.selectedProfessional = action.payload;
        },

        fetchContactSuccessAndSetSelectedProfessional: (state, action: PayloadAction<any[]>) => {
            state.loading = false;
            state.professionals = mapProfessionals(action.payload.filter(contact => contact.contact_type === 'Professional'));

            const previousSelectedId = state.selectedProfessional?.contactId;
            if (previousSelectedId) {
                state.selectedProfessional = state.professionals.find(professional => professional.contactId === previousSelectedId) || null;
            } else {
                state.selectedProfessional = null; // Or set to the first professional in the list, etc.
            }
        },

        setCurrentClientContactId: (state, action: PayloadAction<number>) => {
            state.clientContactId = action.payload;
        },

        setCurrentComClientTab: (state, action: PayloadAction<string>) => {
            state.currentComClientTab = action.payload;
        },

        addComClientProject: (state, action: PayloadAction<ClientProject>) => {
            state.comClientProjectList.push(action.payload);
        },

        clearComClientProject: (state) => {
            state.comClientProjectList = [];
        },

        setSelectedComClient: (state, action: PayloadAction<Client>) => {
            state.selectedComClient = action.payload;
        },

        fetchContactSuccessAndSetSelectedComClient: (state, action: PayloadAction<any[]>) => {
            state.loading = false;
            state.clients = mapClients(action.payload.filter(contact => contact.contact_type === 'Client'));

            const previousSelectedId = state.selectedComClient?.contactId;
            const previousSelectedProjectId = state.selectedClientProject?.id;
            
            if (previousSelectedId) {
                state.selectedComClient = state.clients.find(employee => employee.contactId === previousSelectedId) || null;
                if(previousSelectedProjectId !== undefined){
                    state.selectedClientProject = state.selectedComClient?.projects.find(project => project.id === previousSelectedProjectId) || null;
                }else{
                    state.selectedClientProject = state.selectedComClient?.projects[0] || null;
                }
                
                
            } else {
                state.selectedComClient = null; // Or set to the first employee in the list, etc.
            }
        },

        fetchContactSuccessAndDeleteProjectComClient: (state, action: PayloadAction<any[]>) => {
            state.loading = false;
            state.clients = mapClients(action.payload.filter(contact => contact.contact_type === 'Client'));

            const previousSelectedId = state.selectedComClient?.contactId;

            if (previousSelectedId) {
                state.selectedComClient = state.clients.find(employee => employee.contactId === previousSelectedId) || null;

                const projects: ClientProject[] = state.selectedComClient?.projects!;

                if(projects.length>0){
                    state.selectedClientProject = state.selectedComClient?.projects[0]!;
                }else{
                    state.selectedClientProject = null;
                }
                
                
            } else {
                state.selectedComClient = null; // Or set to the first employee in the list, etc.
            }
        },

        setSelectedComClientProject: (state, action: PayloadAction<ClientProject>) => {
            state.selectedClientProject = action.payload;
        },

        setClientProjectStatus: (state, action: PayloadAction<number>) => {
            state.clientProjectStatus = action.payload;
        },

        setCurrentDomClientTab: (state, action: PayloadAction<string>) => {
            state.currentDomClientTab = action.payload;
        },

        setSelectedDomClient: (state, action: PayloadAction<Client>) => {
            state.selectedDomClient = action.payload;
        },

        fetchContactSuccessAndSetSelectedDomClient: (state, action: PayloadAction<any[]>) => {
            state.loading = false;
            state.clients = mapClients(action.payload.filter(contact => contact.contact_type === 'Client'));

            const previousSelectedId = state.selectedDomClient?.contactId;
            if (previousSelectedId) {
                state.selectedDomClient = state.clients.find(employee => employee.contactId === previousSelectedId) || null;
                
            } else {
                state.selectedComClient = null; // Or set to the first employee in the list, etc.
            }
        },
    }
})

export const {setStatus,setActiveTab, fetchContactStart, fetchContactSuccess, fetchContactFailure, setSelectedEmployee, fetchContactSuccessAndSetSelectedEmployee, setSelectedProfessional, 
    fetchContactSuccessAndSetSelectedProfessional,setCurrentClientContactId, setCurrentComClientTab, addComClientProject, clearComClientProject, setSelectedComClient, 
    fetchContactSuccessAndSetSelectedComClient,fetchContactSuccessAndDeleteProjectComClient, setSelectedComClientProject, setClientProjectStatus, setCurrentDomClientTab, setSelectedDomClient, fetchContactSuccessAndSetSelectedDomClient} = contactSlice.actions;
export default contactSlice.reducer;